// Rollbar singleton + small helpers. Initialized once at module load.
// If VITE_ROLLBAR_ACCESS_TOKEN is unset (typical for local dev), all
// helpers no-op so the app stays totally clean — no SDK chatter, no
// failed reporting calls.
//
// The token is a "post_client_item" scope token. It's safe to ship in
// the browser bundle — Rollbar designs these for client-side use.

import Rollbar from 'rollbar'
import type { Profile } from '../types'

const ACCESS_TOKEN = import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN as string | undefined
const ENVIRONMENT = import.meta.env.MODE // 'development' | 'production'

let instance: Rollbar | null = null

if (ACCESS_TOKEN) {
  instance = new Rollbar({
    accessToken: ACCESS_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: ENVIRONMENT,
    // Filter dev noise from the dashboard — we still want errors locally
    // to surface in the browser console, but they don't belong in
    // Rollbar's prod inbox.
    enabled: ENVIRONMENT === 'production',
    payload: {
      client: {
        javascript: {
          source_map_enabled: true,
          // Rollbar will auto-fetch the .map files from the deployed
          // site if Vite emits them with the standard
          // //# sourceMappingURL comment, which it does by default
          // when build.sourcemap is true.
        },
      },
    },
  })
}

// Read-only accessor for code that needs to call Rollbar directly
// (custom breadcrumbs, manual rollbar.log calls). null when disabled.
export const rollbar = instance

/**
 * Attach the signed-in user to subsequent error reports. Safe to call
 * even when Rollbar isn't configured (no-op).
 */
export function setRollbarUser(profile: Profile | null): void {
  if (!instance || !profile) return
  instance.configure({
    payload: {
      person: {
        id: profile.id,
        email: profile.email,
        username: profile.full_name,
        role: profile.role,
        organization_id: profile.organization_id,
      },
    },
  })
}

/**
 * Clear the user context (call on logout). No-op when Rollbar is off.
 */
export function clearRollbarUser(): void {
  if (!instance) return
  instance.configure({
    payload: {
      person: undefined,
    },
  })
}

/**
 * Manual error report — use for caught errors you want to track that
 * wouldn't naturally bubble to the global handler. Examples:
 *   try { ... } catch (e) { reportError(e, { context: 'order-cancel' }) }
 */
export function reportError(err: unknown, extra?: Record<string, unknown>): void {
  if (!instance) {
    // In dev, log the would-be report so we still see what we'd be sending.
    console.error('[rollbar would report]', err, extra)
    return
  }
  if (err instanceof Error) {
    instance.error(err, extra)
  } else {
    instance.error(String(err), extra)
  }
}
